import { utcToZonedTime } from "date-fns-tz";
import { z } from "zod";

import type { CycleType } from "../../Config";
import { AUTOMATION_TYPES, AutomationType, CYCLE_NAME_MAX_LENGTH, REQUIRED } from "./constants";

const startOfDayInDays = (daysFromToday: number, timezone: string) => {
	const date = utcToZonedTime(new Date(), timezone);
	date.setDate(date.getDate() + daysFromToday);
	date.setHours(0, 0, 0, 0);
	return date;
};

const dayAfter = (date: Date) => {
	const next = new Date(date);
	next.setDate(next.getDate() + 1);
	return next;
};

const baseSettingsShape = {
	cycleName: z
		.string()
		.trim()
		.min(1, REQUIRED)
		.max(CYCLE_NAME_MAX_LENGTH, `Cycle name must be ${CYCLE_NAME_MAX_LENGTH} characters or fewer`),
	lockReviews: z.boolean(),
	startDate: z
		.date()
		.nullable()
		.refine((value) => value !== null, REQUIRED),
};

const manualSettingsSchema = z.object(baseSettingsShape);

const buildAutomatedSettingsSchema = (companyTimezone: string) =>
	z
		.object({
			...baseSettingsShape,
			automationType: z.enum(AUTOMATION_TYPES),
			automationEndDate: z.date().nullable().optional(),
		})
		.superRefine((value, ctx) => {
			if (value.automationType !== "recurring" || !value.automationEndDate) return;

			const tomorrow = startOfDayInDays(1, companyTimezone);

			if (value.automationEndDate < tomorrow) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["automationEndDate"],
					message: "Automation end date must be at least one day in the future",
				});
			} else if (value.startDate && value.automationEndDate < dayAfter(value.startDate)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["automationEndDate"],
					message: "Automation end date must be at least one day after the review cycle start date",
				});
			}
		});

const groupSettingsSchema = z.object({
	...baseSettingsShape,
	group: z
		.object({ id: z.string(), name: z.string() })
		.passthrough()
		.nullable()
		.refine((value) => value !== null, REQUIRED),
});

export const getSettingsSchema = (cycleType: CycleType, companyTimezone: string) => {
	switch (cycleType) {
		case "manual":
			return manualSettingsSchema;
		case "automated":
			return buildAutomatedSettingsSchema(companyTimezone);
		case "group":
			return groupSettingsSchema;
	}
};

export const getSettingsDefaults = (cycleType: CycleType) => {
	const shared = { cycleName: "", lockReviews: true, startDate: null };
	switch (cycleType) {
		case "manual":
			return shared;
		case "automated":
			return { ...shared, automationType: "oneTime" as AutomationType, automationEndDate: null };
		case "group":
			return { ...shared, group: null };
	}
};
